import { useQueryClient } from '@tanstack/react-query';
import { Button, ButtonProps } from 'primereact/button';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
  CopyFootPrintResponse,
  useCopyFootprintDetails,
  useGetFootprintById,
} from '../../../../../../../../hooks/api/footprints';
import useNotificationContext from '../../../../../../../../hooks/notification/useNotificationContext';
import { ToastSeverity } from '../../../../../../../../shared/enums/toast-severity';
import { CREATION_METHOD } from '../../../../../../../../shared/interfaces/IFootprint';

type CopyAsNewButtonProps = {
  footprintId: number;
  productId: number;
} & ButtonProps;

const CopyAsNewButton = (props: CopyAsNewButtonProps): JSX.Element => {
  const { footprintId, productId, ...restProps } = props;
  const { t } = useTranslation();

  const { notify } = useNotificationContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Getting the footprint details
  const { data: footprintDetails, isError } = useGetFootprintById(Number(footprintId), {
    enabled: !!footprintId,
    retry: 0,
  });

  const handleError = useCallback(() => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: t('toastMessages.addPcf.states.failure.detail'),
      life: 5000,
    });
  }, [notify, t]);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['footprints', 'pId', productId.toString()],
    });
  }, [queryClient, productId]);

  const onCopyPCFSuccess = async (response: CopyFootPrintResponse) => {
    if (!response.footprintId || !footprintDetails) return;

    invalidateQueries();

    /* eslint-disable camelcase */

    if (footprintDetails.creation_method === CREATION_METHOD.MANUAL) {
      navigate(`/products/${productId}/pcf/footprint/${response.footprintId}`);
    }

    /* eslint-enable camelcase */
  };

  const { mutate: copyPcf, isLoading: isCopying } = useCopyFootprintDetails({
    onSuccess: onCopyPCFSuccess,
    onError: handleError,
  });

  const handleButtonClick = async () => {
    if (!footprintDetails) return;

    try {
      // we need to copy pcf details and create new pcf for manual method
      if (footprintDetails.creation_method === CREATION_METHOD.MANUAL) {
        await copyPcf({
          pcfId: footprintId,
        });
      } else if (footprintDetails.creation_method === CREATION_METHOD.CALCULATION) {
        // we need to copy pcf details and navigate to new path without creating pcf
        navigate(`/products/${productId}/pcf/footprint/${footprintId}/copy`);
      }
    } catch (error) {
      handleError();
    }
  };

  // If footprint details not found then navigate to 404 page
  if (isError) {
    return <Navigate to='/404' />;
  }

  return (
    <Button
      loading={isCopying}
      text
      label={t('productDetailsPage.tabs.pcfTab.buttons.copyAsNewButton')}
      onClick={handleButtonClick}
      {...restProps}
    />
  );
};

export default CopyAsNewButton;
